import { payoutFor } from "@reagent/meta";
import { CARDS } from "@reagent/content";
import { useGameStore, type RunSpeed } from "../../app/gameStore";
import { ReagentChip } from "../ReagentChip";
import { playCue } from "../../audio/AudioEngine";
import { ForecastPanel } from "./ForecastPanel";
import "./hud.css";

/**
 * Diegetic HUD: physical shop instruments rather than flat overlay chrome —
 * a kW meter with a swinging needle, a hopper that fills toward quota, and a
 * chunky Run switch that visibly throws.
 *
 * It now reads the engine's real phase, so the switch means something: it
 * ends the Play phase and starts the tick clock, and it is dead outside Play.
 */
const SPEEDS: readonly RunSpeed[] = ["slow", "normal", "fast"];

export function Hud() {
  const snapshot = useGameStore((s) => s.snapshot);
  const speed = useGameStore((s) => s.speed);
  const setSpeed = useGameStore((s) => s.setSpeed);
  const startRun = useGameStore((s) => s.startRun);
  const tick = useGameStore((s) => s.tick);
  const advanceFromStatus = useGameStore((s) => s.advanceFromStatus);

  const energyLeft = snapshot.energyBudgetKw - snapshot.energySpentKw;
  const energyFrac = snapshot.energyBudgetKw > 0 ? energyLeft / snapshot.energyBudgetKw : 0;

  const target = sumComposition(snapshot.objective.targetComposition);
  const collected = sumComposition(snapshot.objective.collected);
  const quotaFrac = target > 0 ? Math.min(collected / target, 1) : 0;

  const isOver = snapshot.isRunComplete || snapshot.isRunFailed;
  const isRunPhase = snapshot.phase === "run";
  const canStart = snapshot.phase === "play" && !isOver;

  // Priced live, the same as the end-of-stage screen: the four metrics and the
  // fine are what's actually being decided tick to tick, not just the total.
  const payout = payoutFor(snapshot.tallies, snapshot.isRunComplete);

  return (
    <div className="hud">
      <EnergyMeter fraction={energyFrac} label={`${energyLeft} / ${snapshot.energyBudgetKw} kW`} />

      <QuotaHopper
        fraction={quotaFrac}
        label={`${collected} / ${target}`}
        composition={snapshot.objective.targetComposition}
      />

      <div className="hud__phase">
        <span className="hud__phase-label">phase</span>
        <span className="hud__phase-value">{snapshot.phase}</span>
        <span className="hud__turn">
          turn {snapshot.turn} / {snapshot.maxTurns}
          {isRunPhase && ` · tick ${snapshot.tickInTurn} / ${snapshot.ticksPerTurn}`}
        </span>
        {snapshot.upcomingScriptedEvents.length > 0 ? (
          <span className="hud__telegraph">
            Turn {snapshot.upcomingScriptedEvents[0]!.turn}: {getEventName(snapshot.upcomingScriptedEvents[0]!.kind)}
          </span>
        ) : null}
        {/* Held hazard cards charge a penalty every turn they sit in hand
            (spec 0007 §4); naming it here is what makes it legible rather
            than a mystery kW shortfall the player has to reverse-engineer. */}
        {/* Live building spends from the same ₵ ledger as Setup (spec 0067). */}
        {snapshot.phase === "play" && snapshot.liveBuild !== null && (
          <span className="hud__telegraph">
            {snapshot.setupBudgetCredits - snapshot.setupSpentCredits} ₵ left to build
          </span>
        )}
        {snapshot.deckPenalties.powerPenaltyKw > 0 && (
          <span className="hud__telegraph">-{snapshot.deckPenalties.powerPenaltyKw} kW held penalty</span>
        )}
        {snapshot.deckPenalties.directiveSurchargeKw > 0 && (
          <span className="hud__telegraph">+{snapshot.deckPenalties.directiveSurchargeKw} kW directive surcharge</span>
        )}
        {snapshot.deckPenalties.handSizePenaltyNextTurn > 0 && (
          <span className="hud__telegraph">-{snapshot.deckPenalties.handSizePenaltyNextTurn} card next draw</span>
        )}
        {snapshot.facilityMitigations.length > 0 && (
          <div className="hud__facilities">
            {snapshot.facilityMitigations.map((mitigation) => {
              const cardName = CARDS[mitigation.cardId]?.name ?? mitigation.cardId;
              return (
                <span key={mitigation.cardId} className="hud__facility">
                  {cardName}
                  {mitigation.spent && " (spent)"}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Score runs live rather than only appearing at the end: purity and
          waste are decisions being made every tick, so they need to be
          visible while there is still time to change them. */}
      <div className="hud__score">
        <span className="hud__score-total">{payout.total}₵</span>
        <span className="hud__score-parts">
          yield {payout.yieldCredits}₵ · purity {payout.purityCredits}₵ · speed {payout.speedCredits}₵ · valorized{" "}
          {payout.valorizationCredits}₵ · fine −{payout.dumpingFine}₵ · vent fine −{payout.ventFine}₵
        </span>
      </div>

      {/* Status is its own phase (spec 0017 criterion 9): the turn's ticks
          are spent and the outcome is on screen, but the next hand isn't
          dealt until the player says so — rather than the rollover
          collapsing straight past anything to read. */}
      {snapshot.phase === "status" && (
        <div className="hud__status">
          <span className="hud__status-label">turn {snapshot.turn} settled</span>
          <span className="hud__status-score">{payout.total}₵</span>
          <button
            className="hud__status-continue"
            onClick={() => {
              playCue("uiClick");
              advanceFromStatus();
            }}
          >
            continue
          </button>
        </div>
      )}

      {/* Speed lives in the HUD, not the debug panel: watching one tick at a
          time is how you work out what a rig is doing wrong. */}
      <div className="hud__speed">
        <span className="hud__speed-label">rate</span>
        <div className="hud__speed-row">
          {SPEEDS.map((s) => (
            <button
              key={s}
              className={s === speed ? "is-active" : ""}
              disabled={!isRunPhase}
              onClick={() => {
                playCue("uiClick");
                setSpeed(s);
              }}
            >
              {s}
            </button>
          ))}
          <button disabled={!isRunPhase} onClick={() => setSpeed("paused")}>
            hold
          </button>
          <button disabled={!isRunPhase} onClick={() => tick()}>
            step
          </button>
        </div>
      </div>

      {/* Beside the switch, not behind a phase of its own: what the coming
          turn will cross belongs in the same glance as the lever that commits
          to it (spec 0018). */}
      <ForecastPanel />

      <RunSwitch
        thrown={isRunPhase && speed !== "paused"}
        disabled={!canStart}
        label={isOver ? "done" : isRunPhase ? "running" : "run"}
        onToggle={() => {
          // The clack belongs to the lever, not to the phase change: it should
          // sound even on a turn where the engine has nothing to report.
          playCue("switchThrow");
          startRun();
        }}
      />
    </div>
  );
}

/** Analog kW gauge. The needle sweeps 240 degrees and eases into place. */
function EnergyMeter({ fraction, label }: { fraction: number; label: string }) {
  const angle = -120 + fraction * 240;
  return (
    <div className="gauge">
      <div className="gauge__face">
        <div className="gauge__ticks">
          {Array.from({ length: 9 }, (_, i) => (
            <span key={i} style={{ transform: `rotate(${-120 + i * 30}deg)` }} />
          ))}
        </div>
        <div className="gauge__needle" style={{ transform: `rotate(${angle}deg)` }} />
        <div className="gauge__hub" />
      </div>
      <div className="gauge__label">{label}</div>
      <div className="gauge__caption">power</div>
    </div>
  );
}

/**
 * Collection hopper that physically fills as product lands.
 *
 * The caption names the product with its glyph rather than saying "quota",
 * so the shape a player is trying to make is on screen next to the count of
 * how many they have made.
 */
function QuotaHopper({
  fraction,
  label,
  composition,
}: {
  fraction: number;
  label: string;
  composition: Record<string, number>;
}) {
  return (
    <div className="hopper">
      <div className="hopper__body">
        <div className="hopper__fill" style={{ height: `${fraction * 100}%` }} />
        <div className="hopper__gloss" />
      </div>
      <div className="hopper__label">{label}</div>
      <div className="hopper__caption">
        <ReagentChip composition={composition} />
      </div>
    </div>
  );
}

/** Big tactile switch. Throws with a spring, not a fade. */
function RunSwitch({
  thrown,
  disabled,
  label,
  onToggle,
}: {
  thrown: boolean;
  disabled: boolean;
  label: string;
  onToggle: () => void;
}) {
  return (
    <button
      className={`run-switch${thrown ? " is-thrown" : ""}`}
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={thrown}
    >
      <span className="run-switch__slot">
        <span className="run-switch__lever" />
      </span>
      <span className="run-switch__text">{label}</span>
    </button>
  );
}

function sumComposition(composition: Record<string, number>): number {
  return Object.values(composition).reduce((total, n) => total + n, 0);
}

function getEventName(kind: "powerSurge" | "heatwave" | "seismic"): string {
  const names = {
    powerSurge: "Power Surge",
    heatwave: "Heatwave",
    seismic: "Seismic Event",
  };
  return names[kind];
}
